import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import type { Document } from "mkm-types";
import type { DocumentFilters, FormattedDocument } from "./useDocumentsTypes";
import { GetDocumentParams, GetDocumentResponse } from "mkm-api";
import { toCurrency } from "mkm-avengers";
import { toRefs } from "@vueuse/core";
import { useConfig } from "~/constants";

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const BillableStatus = ["Outstanding", "Overdue"];
const PayableStatus = [...BillableStatus, "Available"];

export interface UseDocumentsState {
  documents: Array<Document> | null;
  documentsSearchResult: Array<FormattedDocument> | null;
  selectedDocuments: Array<FormattedDocument>;
  invoices: Array<Document> | null;
  creditNotes: Array<Document> | null;
  errorFetchingDocuments: boolean;
  loadingDocuments: boolean;
}

const getDocumentStatus = (
  document: Document,
): "Paid" | "Outstanding" | "Overdue" | "Available" | "Redeemed" | "Closed" => {
  const { status, document_type, outstanding, document_due } = document;
  const desc = document_type.desc;

  if (status === "Closed" && outstanding === 0) return desc === "Invoice" ? "Paid" : "Redeemed";

  if (status === "Available") {
    if (desc === "Invoice" && outstanding > 0)
      return dayjs(document_due, "YYYY-MM-DD").isBefore(dayjs()) ? "Overdue" : "Outstanding";

    if (desc === "Credit note" && outstanding < 0) return "Available";
  }

  return status;
};

const formatSearchResult = (documents: Array<Document>): Array<FormattedDocument> => {
  return documents.map((document: Document) => {
    return {
      year: document.year,
      period: document.period,
      glitem: document.glitem,
      document_type: document.document_type.desc === "Credit note" ? "Credit" : document.document_type.desc,
      customer_reference: document.customer_reference,
      document_number: document.document_number,
      document_date: dayjs(document.document_date, "YYYY-MM-DD").format("DD/MM/YYYY"),
      document_due: dayjs(document.document_due, "YYYY-MM-DD").format("DD/MM/YYYY"),
      value: toCurrency({ value: document.value }),
      outstanding: toCurrency({ value: document.outstanding }),
      status: getDocumentStatus(document),
      selected: false,
    };
  });
};

const getStatusVariant = (document: FormattedDocument): "success" | "error" | "warning" | "info" | "disabled" => {
  switch (document.status) {
    case "Paid":
    case "Available":
      return "success";
    case "Outstanding":
      return "warning";
    case "Overdue":
      return "error";
    default:
      return "info";
  }
};

const fetchDocument = async (params: GetDocumentParams): Promise<GetDocumentResponse | null> => {
  const { baseUrl, headers } = useConfig();
  const { data, error } = await useAsyncData("fetchDocument", () =>
    $fetch<GetDocumentResponse>(`${baseUrl}/getDocument`, {
      headers,
      body: JSON.stringify(params),
      method: "POST",
      credentials: "include",
    }),
  );

  useHandleError(error.value);

  return data.value;
};

const useDocuments = () => {
  const { baseUrl, headers } = useConfig();

  const state = useState<UseDocumentsState>(`useDocuments`, () => ({
    documents: null,
    documentsSearchResult: null,
    selectedDocuments: [],
    invoices: null,
    creditNotes: null,
    errorFetchingDocuments: false,
    loadingDocuments: false,
  }));

  const fetchDocuments = async (): Promise<void> => {
    state.value.loadingDocuments = true;

    const { data: documents, error } = await useAsyncData("getInvoices", () =>
      $fetch<Array<Document>>(`${baseUrl}/getInvoices`, {
        headers,
        method: "POST",
        credentials: "include",
      }),
    );

    if (error.value) {
      state.value.documents = null;
      state.value.documentsSearchResult = null;
      state.value.selectedDocuments = [];
      state.value.invoices = null;
      state.value.creditNotes = null;
      state.value.errorFetchingDocuments = true;
      state.value.loadingDocuments = false;

      return;
    }

    if (!documents.value) {
      return;
    }

    state.value.documents = documents.value;
    state.value.documentsSearchResult = formatSearchResult(documents.value);

    state.value.invoices = documents.value.filter((document: Document) => document.document_type.desc === "Invoice");

    state.value.creditNotes = documents.value.filter(
      (document: Document) => document.document_type.desc === "Credit note",
    );

    state.value.errorFetchingDocuments = false;
    state.value.loadingDocuments = false;
  };

  const selectAllDocuments = (selected: boolean): void => {
    state.value.documentsSearchResult?.forEach((document: FormattedDocument) => {
      document.selected = selected;
    });

    state.value.selectedDocuments = selected ? state.value.documentsSearchResult || [] : [];
  };

  const selectPaidDocuments = () => {
    state.value.selectedDocuments = [];

    state.value.documentsSearchResult?.forEach((document: FormattedDocument) => {
      const isPaid = ["Paid", "Redeemed", "Closed"].includes(document.status);

      document.selected = isPaid;

      if (isPaid) state.value.selectedDocuments.push(document);
    });
  };

  const selectUnpaidDocuments = () => {
    state.value.selectedDocuments = [];

    state.value.documentsSearchResult?.forEach((document: FormattedDocument) => {
      const isUnpaid = PayableStatus.includes(document.status);

      document.selected = isUnpaid;

      if (isUnpaid) state.value.selectedDocuments.push(document);
    });
  };

  const deselectDocuments = async () => {
    state.value.selectedDocuments = [];
  };

  const updateSelectedDocuments = (document: FormattedDocument): void => {
    const documentIndex = state.value.selectedDocuments.findIndex(
      (selectedDocument: FormattedDocument) => selectedDocument.document_number === document.document_number,
    );

    if (documentIndex === -1 && document.selected) {
      state.value.selectedDocuments.push(document);

      return;
    }

    if (documentIndex !== -1 && !document.selected) {
      state.value.selectedDocuments.splice(documentIndex, 1);
    }
  };

  const filterDocuments = ({ numberOrJobReference, type, status, startDate, endDate }: DocumentFilters): void => {
    if (!state.value.documents || state.value.documents.length === 0) return;

    selectAllDocuments(false);
    state.value.selectedDocuments = [];

    let searchResult = state.value.documents;

    if (startDate && !endDate) {
      searchResult = searchResult.filter((document: Document) =>
        dayjs(document.document_date, "YYYY-MM-DD").isSameOrAfter(dayjs(startDate)),
      );
    }

    if (!startDate && endDate) {
      searchResult = searchResult.filter((document: Document) =>
        dayjs(document.document_date, "YYYY-MM-DD").isSameOrBefore(dayjs(endDate)),
      );
    }

    if (startDate && endDate) {
      searchResult = searchResult.filter(
        (document: Document) =>
          dayjs(document.document_date, "YYYY-MM-DD").isSameOrAfter(dayjs(startDate)) &&
          dayjs(document.document_date, "YYYY-MM-DD").isSameOrBefore(dayjs(endDate)),
      );
    }

    if (numberOrJobReference) {
      searchResult = searchResult.filter(
        (document: Document) =>
          document.document_number.toString().toLowerCase().includes(numberOrJobReference.toLowerCase()) ||
          document.customer_reference.toLowerCase().includes(numberOrJobReference.toLowerCase()),
      );
    }

    if (type) {
      searchResult = searchResult.filter((document: Document) => document.document_type.desc === type);
    }

    if (status) {
      searchResult = searchResult.filter((document: Document) => document.status === status);
    }

    state.value.documentsSearchResult = formatSearchResult(searchResult);
  };

  const resetFilters = (): void => {
    state.value.documentsSearchResult = state.value.documents ? formatSearchResult(state.value.documents) : [];
    selectAllDocuments(false);
    state.value.selectedDocuments = [];
  };

  return {
    fetchDocument,
    fetchDocuments,
    filterDocuments,
    getStatusVariant,
    resetFilters,
    selectAllDocuments,
    selectPaidDocuments,
    selectUnpaidDocuments,
    updateSelectedDocuments,
    deselectDocuments,
    PayableStatus,
    BillableStatus,
    ...toRefs(state.value),
  };
};

export default useDocuments;
